import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Check, X, Receipt, HandCoins, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface RecordLoanRepaymentSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    loanId: number;
    loanDescription?: string;
    repaymentId?: number | null; // For edit mode
    onSuccess?: () => void;
}

export default function RecordLoanRepaymentSheet({
    open,
    onOpenChange,
    loanId,
    loanDescription,
    repaymentId,
    onSuccess,
}: RecordLoanRepaymentSheetProps) {
    const queryClient = useQueryClient();
    const isEditMode = !!repaymentId;
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    // Removed manual mode state as requested
    const [transactionId, setTransactionId] = useState<number | null>(null);

    // Fetch existing repayment data for edit mode
    const { data: existingRepayment, isLoading: isLoadingRepayment } = useQuery({
        queryKey: ['loan-repayment', repaymentId],
        queryFn: async () => {
            if (!repaymentId) return null;

            const { data, error } = await supabase
                .from('loan_repayments')
                .select(`
                    *,
                    transaction:transactions!transaction_id(id, amount, transaction_at, description, type)
                `)
                .eq('id', repaymentId)
                .single();

            if (error) throw error;
            return data;
        },
        enabled: open && isEditMode,
    });

    // Populate form when editing
    useEffect(() => {
        if (existingRepayment && isEditMode) {
            setTransactionId(existingRepayment.transaction_id);
        }
    }, [existingRepayment, isEditMode]);

    // Reset when closing
    useEffect(() => {
        if (!open) {
            setTransactionId(null);
        }
    }, [open]);

    // Simple dirty check
    const isDirty = transactionId !== null;

    const handleClose = () => {
        if (isDirty && !isEditMode) {
            setShowCloseConfirm(true);
        } else {
            onOpenChange(false);
        }
    };

    const saveRepaymentMutation = useMutation({
        mutationFn: async () => {
            // Transaction Mode Only
            // If editing and we CHANGED the transaction ID, we update the link.
            if (isEditMode && existingRepayment && transactionId === existingRepayment.transaction_id) {
                // No change in transaction link. Just done.
                return { id: repaymentId };
            }

            if (isEditMode && repaymentId) {
                // UPDATE link
                if (transactionId !== existingRepayment?.transaction_id) {
                    const { error: linkError } = await supabase
                        .from('loan_repayments')
                        .update({ transaction_id: transactionId })
                        .eq('id', repaymentId);
                    if (linkError) throw linkError;
                }
            } else {
                // CREATE link
                const { error: linkError } = await supabase
                    .from('loan_repayments')
                    .insert({
                        loan_id: loanId,
                        transaction_id: transactionId
                    });
                if (linkError) throw linkError;
            }

            return { id: transactionId };
        },
        onSuccess: () => {
            toast.success(isEditMode ? 'Repayment updated' : 'Repayment added');
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            queryClient.invalidateQueries({ queryKey: ['loans'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            onOpenChange(false);
            onSuccess?.();
            setShowCloseConfirm(false);
        },
        onError: (error) => {
            toast.error(`Failed: ${error.message}`);
        },
    });

    const handleSubmit = () => {
        if (!transactionId) {
            toast.error('Please select a transaction');
            return;
        }
        saveRepaymentMutation.mutate();
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* Left Sidebar */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-8 relative z-10">
                            <h2 className="text-2xl font-semibold tracking-tight">{isEditMode ? 'Edit Repayment' : 'Add Repayment'}</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {isEditMode ? 'Update the repayment details below.' : 'Link a payment for this advance.'}
                            </p>
                            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                                <Receipt className="h-3 w-3" />
                                Advance #{loanId}
                            </div>
                        </div>

                        <div className="space-y-1 relative z-10 flex-1">
                            <div className="group flex items-center gap-3 p-3 rounded-xl bg-background shadow-sm ring-1 ring-border transition-all duration-300">
                                <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary text-primary-foreground transition-colors duration-300">
                                    <Receipt className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-sm text-foreground">
                                        Repayment Details
                                    </p>
                                    <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                        Amount & Date
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-auto w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Cancel & Close
                        </Button>
                    </div>

                    {/* Right Content */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile Header */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">{isEditMode ? 'Edit Repayment' : 'Add Repayment'}</h2>
                                <p className="text-xs text-muted-foreground">Advance #{loanId}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Main Form Area */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10">
                            <div className="h-full max-w-lg mx-auto">
                                {isLoadingRepayment ? (
                                    <div className="flex items-center justify-center py-12">
                                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Select Transaction *</Label>
                                            <TransactionSelector
                                                value={transactionId}
                                                onChange={(id) => setTransactionId(id)}
                                                defaultType="CREDIT"
                                                placeholder="Select payment transaction..."
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-lg mx-auto flex items-center justify-end gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    disabled={saveRepaymentMutation.isPending}
                                    className="w-24 rounded-full"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    onClick={handleSubmit}
                                    disabled={saveRepaymentMutation.isPending}
                                    className="w-40 rounded-full shadow-lg hover:shadow-emerald-500/25 transition-all bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                    {saveRepaymentMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4 mr-2" />
                                    )}
                                    {isEditMode ? 'Save Changes' : 'Record'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onOpenChange(false)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
